import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/i18nContext';
import { ROLE_LABELS, ROLES } from '../../constants/roles';
import Button from '../../components/ui/Button';
import '../../styles/InvitationsPage.css';

const InvitationsPage = () => {
  const { api, user: currentUser } = useAuth();
  const { t } = useI18n();

  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [msg, setMsg]                 = useState('');

  const [email, setEmail]   = useState('');
  const [role, setRole]     = useState('2');
  const [sending, setSend]  = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/invitations');
      setInvitations(r.data.invitations);
    } catch { setError(t('common.error')); }
    finally { setLoading(false); }
  }, [api, t]);

  useEffect(() => { load(); }, [load]);

  const handleSend = async (e) => {
    e.preventDefault();
    setSend(true); setMsg(''); setError('');
    try {
      await api.post('/admin/invitations', { email, role: Number(role) });
      setMsg(`✅ ${t('admin.invitationSent')}: ${email}`);
      setEmail('');
      load();
    } catch (err) {
      setError(err.response?.data?.error || t('admin.revokeFailed'));
    } finally { setSend(false); }
  };

  const handleRevoke = async (id) => {
    try {
      await api.delete(`/admin/invitations/${id}`);
      setInvitations(inv => inv.filter(i => i.id !== id));
    } catch { setError(t('admin.revokeFailed')); }
  };

  const isSuperadmin = currentUser?.role === ROLES.SUPERADMIN;

  const statusBadge = (inv) => {
    if (inv.used_at) return <span className="inv-badge used">{t('admin.used')}</span>;
    if (new Date(inv.expires_at) < new Date()) return <span className="inv-badge expired">{t('admin.expired')}</span>;
    return <span className="inv-badge pending">{t('admin.active')}</span>;
  };

  return (
    <div className="inv-page">
      <h1 className="inv-title">📨 {t('admin.invitations')}</h1>

      {/* SEND FORM */}
      <div className="inv-form-card">
        <h3>{t('admin.sendInvitation')}</h3>
        {msg   && <p className="inv-msg success">{msg}</p>}
        {error && <p className="inv-msg error">❌ {error}</p>}
        <form onSubmit={handleSend} className="inv-form">
          <input
            type="email"
            placeholder={t('auth.email')}
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            className="inv-input"
          />
          <select value={role} onChange={e => setRole(e.target.value)} className="inv-select">
            {isSuperadmin && <option value="0">Superadmin</option>}
            <option value="1">Admin</option>
            <option value="2">User</option>
            <option value="3">Agent</option>
          </select>
          <Button type="submit" variant="solid" disabled={sending}>
            {sending ? t('admin.sending') : `📨 ${t('admin.send')}`}
          </Button>
        </form>
      </div>

      {/* LIST */}
      <div className="inv-list-card">
        <h3>{t('admin.invitationsList')}</h3>
        {loading ? (
          <p className="inv-empty">{t('common.loading')}</p>
        ) : invitations.length === 0 ? (
          <p className="inv-empty">{t('admin.noInvitations')}</p>
        ) : (
          <div className="inv-table-wrap">
            <table className="inv-table">
              <thead>
                <tr>
                  <th>{t('auth.email')}</th>
                  <th>{t('admin.role')}</th>
                  <th>{t('admin.status')}</th>
                  <th>{t('admin.invitedBy')}</th>
                  <th>{t('admin.expires')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {invitations.map(inv => (
                  <tr key={inv.id}>
                    <td className="inv-email">{inv.email}</td>
                    <td>
                      <span className="inv-role-badge">
                        {ROLE_LABELS[inv.role] ?? `Role ${inv.role}`}
                      </span>
                    </td>
                    <td>{statusBadge(inv)}</td>
                    <td className="inv-by">{inv.invited_by_email || '—'}</td>
                    <td className="inv-date">
                      {new Date(inv.expires_at).toLocaleDateString()}
                    </td>
                    <td>
                      {!inv.used_at && new Date(inv.expires_at) > new Date() && (
                        <Button variant="danger" size="sm" onClick={() => handleRevoke(inv.id)}>
                          {t('admin.revoke')}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvitationsPage;
